import { useAnalytics } from '@app/services/analytics/AnalyticsContext';
import { PurchasedGoodsInsights } from '@shared/api/types';
import SimpleAreaChart from '@shared/components/chart/simple-area-chart';
import { DashboardSection } from '@shared/components/dashboard/dashboard-section';
import SimpleSelect from '@shared/components/simple-select';
import { Card } from '@shared/components/ui/card';
import { ChartConfig } from '@shared/components/ui/chart';
import {
  formatCost,
  formatTonnes,
  getCurrencySymbol,
} from '@shared/lib/format';
import { format, parseISO } from 'date-fns';
import { Icons } from '@shared/components/content/icons';
import { useMemo } from 'react';
import {
  InsightsChartType,
  useInsightsChartType,
} from '../hooks/use-insights-filters';
import { ChartAxisTitles } from './chart-axis-titles';
import { isCompletedMonth } from '../lib/dense-series';
import { InsightsNoSpendData } from './insights-no-spend-data';

type OverallChartType = Extract<InsightsChartType, 'emissions' | 'spend'>;

const CHART_LABELS: Record<OverallChartType, string> = {
  emissions: 'Emissions over time',
  spend: 'Spend over time',
};

const PREVIOUS_YEAR_COLOR = 'var(--muted-foreground)';

type Props = {
  data?: PurchasedGoodsInsights;
  currency?: string | null;
  /** Whether spend is available; false → the spend chart shows an empty state. */
  hasSpendData: boolean;
  /** Without the cost feature the spend chart stays listed but locked. */
  hasCostFeature: boolean;
};

/**
 * Overall over-time chart: emissions or spend for the selected period, each
 * with a dashed previous-year comparison line. Shown when no grouping is
 * active; grouped multi-series charts arrive in a later phase.
 */
export function InsightsOverTimeSection({
  data,
  currency,
  hasSpendData,
  hasCostFeature,
}: Props) {
  const [chartType, setChartType] = useInsightsChartType();
  const { track } = useAnalytics();
  // The Overall view offers only the two time series; any grouped chart type
  // lingering in the URL falls back to emissions here, as does spend for a
  // locked account, whatever the URL says.
  const overallType: OverallChartType =
    chartType === 'spend' && hasCostFeature ? 'spend' : 'emissions';
  const isSpend = overallType === 'spend';
  const symbol = getCurrencySymbol(currency) || '£';

  // The series is dense (one point per month of the window). Completed months
  // with no purchases plot as zero; months still ahead stay unplotted so the
  // line ends at "now" while the axis spans the whole window — the dashed
  // previous-year line keeps going, showing the trajectory to compare against.
  // A completed month with activity but no spend value is missing data, not
  // zero spend, so it stays a gap on the spend chart.
  const chartData = useMemo(
    () =>
      [...(data?.emissionsOverTime ?? [])]
        .sort((a, b) => a.date.localeCompare(b.date))
        .map((point) => {
          const noActivity = point.emissionsTonnes == null;
          const zeroWhenCompleted =
            noActivity && isCompletedMonth(point.date) ? 0 : null;
          return {
            label: format(parseISO(point.date), 'MMM yy'),
            emissions: point.emissionsTonnes ?? zeroWhenCompleted,
            emissionsPreviousYear: point.emissionsTonnesPreviousYear,
            spend:
              point.spend != null ? Number(point.spend) : zeroWhenCompleted,
            spendPreviousYear:
              point.spendPreviousYear != null
                ? Number(point.spendPreviousYear)
                : null,
          };
        }),
    [data?.emissionsOverTime]
  );

  const chartConfig: ChartConfig = isSpend
    ? {
        spend: { label: 'This period', color: 'var(--spend-400)' },
        spendPreviousYear: {
          label: 'Previous year',
          color: PREVIOUS_YEAR_COLOR,
        },
      }
    : {
        emissions: {
          label: 'This period',
          color: 'var(--chart-food-and-drink)',
        },
        emissionsPreviousYear: {
          label: 'Previous year',
          color: PREVIOUS_YEAR_COLOR,
        },
      };

  const description = isSpend
    ? 'Explore your purchasing spend over time, compared with the previous year where available.'
    : 'Explore your purchasing emissions over time, compared with the previous year where available.';

  return (
    <DashboardSection
      title={CHART_LABELS[overallType]}
      description={description}
      actions={
        <SimpleSelect
          className="w-52"
          options={(Object.keys(CHART_LABELS) as OverallChartType[]).map(
            (key) => {
              const locked = key === 'spend' && !hasCostFeature;
              return {
                value: CHART_LABELS[key],
                disabled: locked,
                icon: locked ? (
                  <Icons.lock className="size-3.5 text-gray-400" />
                ) : undefined,
              };
            }
          )}
          value={CHART_LABELS[overallType]}
          onValueChange={(label) => {
            const next =
              (Object.keys(CHART_LABELS) as OverallChartType[]).find(
                (key) => CHART_LABELS[key] === label
              ) ?? 'emissions';
            void setChartType(next);
            track('Switched PG Insights Chart Type', {
              chart_type: CHART_LABELS[next],
              explore_dimension: 'Overall',
            });
          }}
        />
      }
    >
      <Card className="p-5">
        {isSpend && !hasSpendData ? (
          <InsightsNoSpendData />
        ) : (
          <div className="h-80 w-full">
            <ChartAxisTitles
              yTitle={isSpend ? `Spend (${symbol})` : 'Emissions (tCO₂e)'}
            >
              <SimpleAreaChart
                chartConfig={chartConfig}
                chartData={chartData}
                className="aspect-auto size-full"
                nameKey="label"
                dataKey={isSpend ? 'spend' : 'emissions'}
                dashedKeys={
                  isSpend ? ['spendPreviousYear'] : ['emissionsPreviousYear']
                }
                dot
                strokeWidth={2}
                fillOpacity={0.25}
                margin={{ top: 8, right: 24, left: 8, bottom: 0 }}
                YAxisProps={{ width: 52, tickMargin: 4 }}
                tooltipValueFormatter={(value) =>
                  isSpend
                    ? formatCost(Number(value), currency)
                    : formatTonnes(Number(value))
                }
              />
            </ChartAxisTitles>
          </div>
        )}
      </Card>
    </DashboardSection>
  );
}
